import type { AdoptRequest } from '../../domain/adopt.js';
import { countOf } from '../../domain/labels.js';
import { countWord } from '../../domain/pluralise.js';
import { rowLabel, type RowLabel } from '../../domain/rowLabel.js';
import {
    adoptRecordsFor,
    childTypeOf,
    ENTITY_TYPE_ORDER,
    ENTITY_TYPES,
    type EntityType,
} from '../../domain/entityTypes.js';
import type { SelectedCell } from '../Matrix/types.js';

/** The one selected record's label (D4), or null when zero or several are selected. */
export const singleSelectionLabel = (cells: SelectedCell[]): RowLabel | null =>
    cells.length === 1 ? rowLabel(cells[0]!.title, cells[0]!.key) : null;

/** A label on one line of plain text: the name, then the id in parentheses; or the id alone. */
const inlineText = (label: RowLabel): string => (label.secondary ? `${label.label} (${label.secondary})` : label.label);

/**
 * The left side of the selection bar: "2 studios · 403 games under them", or the record itself
 * when exactly one is selected ("Studio One (studio-1) · 312 games under it").
 */
export const summariseSelection = (cells: SelectedCell[]): string => {
    if (cells.length === 0) return '';
    const single = singleSelectionLabel(cells);
    if (single) {
        const under = cells[0]!.childCount ?? 0;
        const childType = childTypeOf(cells[0]!.type);
        return under > 0 && childType
            ? `${inlineText(single)} · ${countOf(childType, under)} under it`
            : inlineText(single);
    }
    const parts: string[] = [];
    for (const type of ENTITY_TYPE_ORDER) {
        const ofType = cells.filter((c) => c.type === type);
        if (ofType.length === 0) continue;
        parts.push(countOf(type, ofType.length));
        const childType = childTypeOf(type);
        const under = ofType.reduce((s, c) => s + (c.childCount ?? 0), 0);
        if (under > 0 && childType)
            parts.push(`${countOf(childType, under)} under ${ofType.length === 1 ? 'it' : 'them'}`);
    }
    return parts.join(' · ');
};

/** The selection's rows, from an environment's inbox, as one request per type, parents first. */
export const adoptRequestsForSelection = (environment: string, cells: SelectedCell[]): AdoptRequest[] =>
    ENTITY_TYPE_ORDER.flatMap((type) => {
        const keys = cells.filter((c) => c.type === type).map((c) => c.key);
        return keys.length > 0 ? [adoptRecordsFor(type, environment, keys)] : [];
    });

/** One record the selection bar's **Delete…** removes from master. */
export type DeleteTarget = { type: EntityType; key: string; title: string | null; expectedRevision: number };

/**
 * The selection as master deletes, parents first. A record whose parent is selected too is left
 * out — the parent's delete cascades to it, and a second delete of it would fail as already gone.
 * A record ticked without a revision cannot be guarded, so it is skipped.
 */
export const deleteTargetsForSelection = (cells: SelectedCell[]): DeleteTarget[] => {
    const selected = new Set(cells.map((c) => `${c.type}:${c.key}`));
    const parentSelected = (c: SelectedCell): boolean => {
        const { parentType, parentKeyOf } = ENTITY_TYPES[c.type];
        const parentKey = parentKeyOf(c.key);
        return parentType !== null && parentKey !== null && selected.has(`${parentType}:${parentKey}`);
    };
    return ENTITY_TYPE_ORDER.flatMap((type) =>
        cells
            .filter((c) => c.type === type && c.revision !== undefined && !parentSelected(c))
            .map((c) => ({ type, key: c.key, title: c.title ?? null, expectedRevision: c.revision! })),
    );
};

/** What the selection's delete did: master records removed (cascades included), and what it could not delete. */
export type DeleteManyResult = { deleted: number; failed: { label: string; message: string }[] };

/** The notification after the selection's delete. */
export const deleteManyOutcome = ({ deleted, failed }: DeleteManyResult) => {
    const unchanged = 'Environment records were not changed.';
    const removed = countWord(deleted, 'master record');
    if (failed.length === 0)
        return { color: 'gray', title: 'Deleted from master', message: `${removed} deleted. ${unchanged}` };
    const notDeleted = failed.map((f) => `${f.label} — ${f.message}`).join('; ');
    return {
        color: 'red',
        title: deleted > 0 ? 'Partly deleted from master' : 'Nothing deleted from master',
        message: `${removed} deleted; not deleted: ${notDeleted}. ${unchanged}`,
    };
};

/** A target's label on one line of plain text, for the outcome. */
export const deleteTargetText = (target: DeleteTarget): string => inlineText(rowLabel(target.title, target.key));
